import type { UserSearchHit } from '@citibox-panel/api-client';
import { USER_COURIER_LABEL } from '../../../lib/userCourierLabels';

type AccountType = UserSearchHit['accountType'];

// The three role ids the panel labels itself: «Cliente», «Transportista Courier» and «Courier
// Compartido» in the accounts table. Every other role is shown by the name the database gives it,
// so a role created tomorrow appears on the ficha without a panel release.
const USER_ROLE_ID = 5;
const COURIER_ROLE_IDS: readonly number[] = [24, 29];

// Whether the ficha treats the account as a courier's: the reason-bearing block and baja, the
// unblock, and their copy all hang off this one predicate, over the same two ids the strip labels
// «Mensajero». No role delivered → false, so an account the panel cannot classify behaves as today.
export function isCourierAccount(accountType: AccountType): boolean {
  return accountType !== null && COURIER_ROLE_IDS.includes(accountType.id);
}

// What the strip says for an account, or null when there is nothing trustworthy to say: no role
// delivered (a backend not yet deployed) or a role without a name. Never a placeholder.
export function accountTypeLabel(accountType: AccountType): string | null {
  if (accountType === null) return null;
  if (accountType.id === USER_ROLE_ID) return USER_COURIER_LABEL.user;
  if (isCourierAccount(accountType)) return USER_COURIER_LABEL.courier;
  const name = accountType.name?.trim() ?? '';
  return name === '' ? null : name;
}

// The analytics class: categorical and closed, never the database name, so no free text from the
// accounts table reaches telemetry. 'unknown' alone measures the contract (the role did not
// arrive, or arrived without a name); 'other' is a role that arrived and is neither of the two.
export type AccountTypeClass = 'user' | 'courier' | 'other' | 'unknown';

export function accountTypeClass(accountType: AccountType): AccountTypeClass {
  if (accountType === null) return 'unknown';
  if (accountType.id === USER_ROLE_ID) return 'user';
  if (isCourierAccount(accountType)) return 'courier';
  return accountTypeLabel(accountType) === null ? 'unknown' : 'other';
}
